import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { AuditResult } from '@/lib/schemas';
import { Button } from './ui/button';
import { CalendarDays, Sparkles } from 'lucide-react';

interface ScoreCardProps {
  result: AuditResult;
  companyName: string;
}

export function ScoreCard({ result, companyName }: ScoreCardProps) {
  const progressPercentage = (result.score / 60) * 100;
  
  const getScoreColor = (score: number) => {
    if (score <= 20) return '#dc2626'; // Red for urgent
    if (score <= 40) return '#F47725'; // Brand orange for amber
    return '#94CD39'; // Brand green for good
  };
  
  const getBadgeClasses = (score: number) => {
    if (score <= 20) return 'bg-red-100 text-red-700 border border-red-200';
    if (score <= 40) return 'bg-brand-orange/10 text-brand-orange border border-brand-orange/30';
    return 'bg-brand-green/10 text-brand-green border border-brand-green/30';
  };

  return (
    <Card className="mb-12 border-0 rounded-3xl shadow-2xl bg-white/95 backdrop-blur-sm">
      <CardHeader className="px-8 pt-8 pb-4 text-center sm:text-left space-y-5">
        <div className="flex justify-center sm:justify-start">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.28em] text-brand-orange">
            <Sparkles className="h-4 w-4" />
            Digital Snapshot
          </span>
        </div>
        <CardTitle className="space-y-2 text-3xl sm:text-4xl font-bold tracking-tight">
          <span className="block bg-gradient-to-r from-brand-dark via-brand-orange to-brand-green bg-clip-text text-transparent">
            Audit Results
          </span>
          <span className="block text-2xl sm:text-[32px] text-brand-dark">
            for {companyName}
          </span>
        </CardTitle>
        <p className="text-sm sm:text-base text-gray-500">
          Your personalized efficiency scorecard with priority insights and next-step recommendations.
        </p>
      </CardHeader>
      <CardContent className="px-8 pb-10 space-y-12">
        <div className="flex flex-col lg:flex-row gap-10">
          <div className="flex-1 rounded-2xl bg-gradient-to-br from-gray-50 via-white to-gray-50 px-8 py-10 text-center lg:text-left shadow-sm">
            <div className="text-6xl sm:text-7xl font-extrabold" style={{ color: getScoreColor(result.score) }}>
              {result.score}/60
            </div>
            <div className="mt-6 space-y-4">
              <Progress value={progressPercentage} className="h-4 rounded-full" />
              <div className="text-lg text-gray-600 font-semibold">
                {Math.round(progressPercentage)}% Digital Efficiency Score
              </div>
            </div>
          </div>

          <div className="flex-1 rounded-2xl border border-gray-200 bg-white px-8 py-10 space-y-4 text-left shadow-sm">
            <div className={`inline-flex items-center gap-2 rounded-lg px-4 py-1.5 text-base font-semibold ${getBadgeClasses(result.score)}`}>
              {result.zone}
            </div>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              {result.summary}
            </p>
          </div>
        </div>
        <div className="rounded-xl bg-gradient-to-r from-[#9be65a] via-[#6fcf4f] to-[#34a853] px-8 sm:px-12 py-10 text-center text-white shadow-[0_18px_40px_-18px_rgba(52,168,83,0.7)] space-y-4">
          <Button
            asChild
            size="lg"
            className="mx-auto inline-flex items-center justify-center gap-3 rounded-xl bg-white px-8 py-3 text-lg font-semibold text-[#2b7a2f] shadow-[0_12px_24px_-10px_rgba(255,255,255,0.35)] transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:text-[#2b7a2f] hover:shadow-[0_18px_36px_-12px_rgba(255,255,255,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f47725]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
          >
            <a
              href="https://cal.com/advissoftware/digitalisation-audit-consultation"
              target="_blank"
              rel="noopener noreferrer"
            >
              <CalendarDays className="h-5 w-5" />
              <span>Book my free 20-mins walkthrough</span>
            </a>
          </Button>
          <p className="text-sm font-medium text-white/85">
            Free • 20 minutes • No obligation
          </p>
        </div>
      </CardContent>
    </Card>
  );
}